// src/pages/FundPlanner/reports/buildPortfolioPositionsReportData.js

import {
  buildHoldingsHierarchy,
  flattenHierarchy,
  getDisplayYield,
} from "pages/FundPlanner/components/Portfolios/portfolioReportPositions";
import { getPortfolioPeriodPdfLabel } from "./portfolioPeriodPdfLabels";

function toNumberOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatDate(value) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return "";
  const [yyyy, mm, dd] = String(value).split("-");
  return `${Number(mm)}/${Number(dd)}/${yyyy}`;
}

function formatPercentFromPercentValue(value, digits = 3) {
  const n = toNumberOrNull(value);
  if (n == null) return "";
  return `${n.toFixed(digits)}%`;
}

function formatPercentFromDecimal(value, digits = 3) {
  const n = toNumberOrNull(value);
  if (n == null) return "";
  return `${(n * 100).toFixed(digits)}%`;
}

function formatDays(value) {
  const n = toNumberOrNull(value);
  if (n == null) return "";
  return Math.round(n).toLocaleString();
}

function formatPrice(value, digits = 4) {
  const n = toNumberOrNull(value);
  if (n == null) return "";
  return n.toFixed(digits);
}

function formatNumber(value, digits = 2) {
  const n = toNumberOrNull(value);
  if (n == null) return "";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function labelForRow(row) {
  if (row.row_type === "portfolio") return `Portfolio: ${row.label || ""}`;
  if (row.row_type === "fund_type") return `  Type: ${row.label || ""}`;
  if (row.row_type === "fund") return `    Fund: ${row.label || ""}`;
  const pendingLabel = row.is_pending_settlement ? " (Pending Settlement)" : "";
  return `      ${row.display_name || ""}${pendingLabel}`;
}

function allExpandedKeys(nodes) {
  const keys = new Set();

  function visit(node) {
    if (node?.is_expandable && node?.key) keys.add(node.key);
    for (const child of node?.children || []) visit(child);
  }

  for (const node of nodes || []) visit(node);
  return keys;
}

function mapReportRow(row, reportMode) {
  const isPosition = row.row_type === "position";
  const metrics = row.metrics || {};

  return {
    row_type: row.row_type,
    level_name: labelForRow(row),
    settlement_date: isPosition ? formatDate(row.settlement_date) : "",
    cusip_or_id: isPosition ? row.cusip_or_id || "" : "",
    issuer_name: isPosition ? row.issuer_name || "" : "",
    rating_rollup: isPosition ? row.rating_rollup || "" : "",
    amount: isPosition
      ? formatNumber(row.current_amount ?? row.amount ?? row.par_amount, 2)
      : formatNumber(metrics.totalPar, 2),
    coupon: isPosition
      ? formatPercentFromPercentValue(row.coupon, 3)
      : "",
    maturity_date: isPosition ? formatDate(row.maturity_date) : "",
    wam_maturity_days: isPosition
      ? formatDays(row.days_to_maturity ?? row.days_to_effective_maturity)
      : formatDays(metrics.weightedAvgMaturityDays),
    first_call_date: isPosition ? formatDate(row.first_call_date) : "",
    last_price: isPosition ? formatPrice(row.price_mid, 4) : "",
    market_value: isPosition
      ? formatNumber(row.market_value, 2)
      : formatNumber(metrics.totalMarketValue, 2),
    purchase_price: isPosition ? formatPrice(row.purchase_price, 4) : "",
    original_cost: isPosition
      ? formatNumber(row.original_cost, 2)
      : formatNumber(metrics.totalOriginalCost, 2),
    unrealized_gain_loss: isPosition
      ? formatNumber(row.unrealized_gain_loss, 2)
      : formatNumber(metrics.totalUnrealizedGainLoss, 2),
    accrued_interest: isPosition
      ? formatNumber(row.accrued_interest, 2)
      : formatNumber(metrics.totalAccruedInterest, 2),
    book_value: isPosition
      ? formatNumber(row.book_value, 2)
      : formatNumber(metrics.totalBookValue, 2),
    net_income: isPosition
      ? formatNumber(row.net_income, 2)
      : formatNumber(metrics.totalNetIncome, 2),
    ytm: isPosition ? formatPercentFromDecimal(getDisplayYield(row, reportMode), 3) : "",
  };
}

export const portfolioPositionsPdfColumns = [
  { key: "level_name", label: "Level / Name", align: "left" },
  { key: "settlement_date", label: "Settle", align: "left" },
  { key: "cusip_or_id", label: "CUSIP / ID", align: "left" },
  { key: "issuer_name", label: "Issuer", align: "left" },
  { key: "rating_rollup", label: "Rating", align: "left" },
  { key: "amount", label: "Par/Face Amt", align: "right" },
  { key: "coupon", label: "Coupon", align: "right" },
  { key: "maturity_date", label: "Maturity", align: "left" },
  { key: "wam_maturity_days", label: "WAM/Mat Days", align: "right" },
  { key: "first_call_date", label: "Call", align: "left" },
  { key: "last_price", label: "Last Px", align: "right" },
  { key: "market_value", label: "Market Value", align: "right" },
  { key: "purchase_price", label: "Purch Px", align: "right" },
  { key: "original_cost", label: "Orig Cost", align: "right" },
  { key: "unrealized_gain_loss", label: "Unrlzd G/L", align: "right" },
  { key: "accrued_interest", label: "Accrued", align: "right" },
  { key: "book_value", label: "Book Value", align: "right" },
  { key: "net_income", label: "Net Income", align: "right" },
  { key: "ytm", label: "Yield", align: "right" },
];

export function buildPortfolioPositionsReportData({
  rows = [],
  asOfDate = "",
  reportMode = "single",
  startDate = "",
  endDate = "",
  orgName = "",
  portfolioName = "",
  reportTitle = "FundPlanner - Portfolio Positions",
} = {}) {
  const hierarchy = buildHoldingsHierarchy(rows);
  const flattenedRows = flattenHierarchy(hierarchy, allExpandedKeys(hierarchy));
  const positionRows = flattenedRows.filter((row) => row.row_type === "position");
  const positionCount = positionRows.filter((row) => !row.is_pending_settlement).length;
  const pendingSettlementCount = positionRows.filter(
    (row) => row.is_pending_settlement
  ).length;
  const isPeriodReport = reportMode === "period" && startDate && endDate;

  return {
    meta: {
      reportTitle,
      orgName,
      portfolioName,
      reportMode,
      asOfDate,
      startDate,
      endDate,
      asOfLabel: isPeriodReport
        ? getPortfolioPeriodPdfLabel(startDate, endDate)
        : asOfDate
          ? `As of ${formatDate(asOfDate)}`
          : "",
      fileDate: endDate || asOfDate || new Date(),
      positionCount,
      pendingSettlementCount,
    },
    positionsTable: {
      title: "Positions",
      columns:
        reportMode === "period"
          ? portfolioPositionsPdfColumns
          : portfolioPositionsPdfColumns.filter((column) => column.key !== "net_income"),
      rows: flattenedRows.map((row) => mapReportRow(row, reportMode)),
    },
  };
}

export default buildPortfolioPositionsReportData;
